/**
 * The MCP tab's model (components/mcp): a form from a tool's JSON schema (or a prompt's arguments) and the arguments
 * it gives, a server's key and label, the session's history of calls, the token the person gives (this browser
 * session only) and the "This server needs a sign-in" helpers.
 *
 * A form field per top-level property: text (an `enum` is a choice), number / integer, a yes/no for a boolean, and JSON
 * for an array, an object or a union (`anyOf` / `oneOf`). A schema with no properties, or one the form cannot show,
 * is edited as raw JSON.
 */

const HISTORY_MAX = 30;
const PRIMITIVES = ['string', 'number', 'integer', 'boolean'];

/** The schema's type, the first that is not "null" when it lists several. */
function typeOf(schema) {
  const { type } = schema || {};
  if (Array.isArray(type)) { return type.find((t) => t !== 'null') || 'string'; }
  return type || (schema && schema.enum ? 'string' : null);
}

/** The form's fields of a JSON schema: `[{ name, kind: 'text'|'number'|'integer'|'boolean'|'enum'|'json', required, description, schema, options? }]`. */
export function schemaFields(schema) {
  const properties = schema && typeof schema === 'object' && schema.properties && typeof schema.properties === 'object' ? schema.properties : {};
  const required = new Set(Array.isArray(schema?.required) ? schema.required : []);
  return Object.entries(properties).map(([name, prop]) => {
    const type = typeOf(prop);
    let kind = 'json';
    if (Array.isArray(prop?.enum) && prop.enum.every((v) => PRIMITIVES.includes(typeof v) || v === null)) { kind = 'enum'; } else if (type === 'string') { kind = 'text'; } else if (type === 'number' || type === 'integer' || type === 'boolean') { kind = type; }
    return {
      name,
      kind,
      type: type || 'any',
      required: required.has(name),
      description: prop?.description || prop?.title || '',
      schema: prop || {},
      ...(kind === 'enum' ? { options: prop.enum } : {}),
    };
  });
}

/** A prompt's arguments as form fields (they are all text). */
export const promptFields = (prompt) => (prompt?.arguments || []).map((arg) => ({
  name: arg.name, kind: 'text', type: 'string', required: Boolean(arg.required), description: arg.description || '', schema: {},
}));

/** Whether a schema is better edited as raw JSON (no properties, or extra ones allowed with none listed). */
export const prefersRaw = (schema, fields) => fields.length === 0 && Boolean(schema && (schema.type === 'object' || schema.properties) && schema.additionalProperties !== false && !schema.properties);

const exampleOf = (schema, depth = 0) => {
  if (!schema || depth > 4) { return null; }
  if (schema.default !== undefined) { return schema.default; }
  if (Array.isArray(schema.enum)) { return schema.enum[0]; }
  const type = typeOf(schema);
  if (type === 'object') { return Object.fromEntries(Object.entries(schema.properties || {}).map(([k, v]) => [k, exampleOf(v, depth + 1)])); }
  if (type === 'array') { return schema.items ? [exampleOf(schema.items, depth + 1)] : []; }
  if (type === 'number' || type === 'integer') { return 0; }
  if (type === 'boolean') { return false; }
  if (type === 'string') { return ''; }
  return null;
};

/** The form's first values: each field's default, else empty (a JSON field: an example of its shape). */
export function initialValues(fields) {
  return Object.fromEntries(fields.map((field) => {
    const given = field.schema.default;
    if (field.kind === 'boolean') { return [field.name, given === true]; }
    if (field.kind === 'json') { return [field.name, given !== undefined ? JSON.stringify(given, null, 2) : '']; }
    return [field.name, given === undefined || given === null ? '' : String(given)];
  }));
}

/** An example of the whole arguments object, for the raw JSON editor. */
export const exampleArgs = (schema) => JSON.stringify(exampleOf({ type: 'object', ...(schema || {}) }) || {}, null, 2);

/**
 * The arguments the form gives: `{ args, errors }` (errors by field). An empty optional field is left out; a required
 * one is an error; numbers and JSON are parsed; a choice keeps the type of its option.
 */
export function formToArgs(fields, values) {
  const args = {};
  const errors = {};
  for (const field of fields) {
    const raw = values[field.name];
    if (field.kind === 'boolean') {
      if (raw === true || field.required) { args[field.name] = raw === true; }
      continue;
    }
    const text = raw === undefined || raw === null ? '' : String(raw);
    if (!text.trim()) {
      if (field.required) { errors[field.name] = 'Required'; }
      continue;
    }
    if (field.kind === 'number' || field.kind === 'integer') {
      const n = Number(text);
      if (!Number.isFinite(n) || (field.kind === 'integer' && !Number.isInteger(n))) { errors[field.name] = field.kind === 'integer' ? 'A whole number' : 'A number'; } else { args[field.name] = n; }
    } else if (field.kind === 'enum') {
      const match = field.options.find((option) => String(option) === text);
      args[field.name] = match === undefined ? text : match;
    } else if (field.kind === 'json') {
      try { args[field.name] = JSON.parse(text); } catch { errors[field.name] = 'Not valid JSON'; }
    } else {
      args[field.name] = text;
    }
  }
  return { args, errors };
}

/** Raw JSON arguments: `{ args }` or `{ error }` (they must be an object). */
export function parseRawArgs(text) {
  if (!String(text || '').trim()) { return { args: {} }; }
  try {
    const value = JSON.parse(text);
    if (!value || typeof value !== 'object' || Array.isArray(value)) { return { error: 'The arguments must be a JSON object' }; }
    return { args: value };
  } catch (error) {
    return { error: `Not valid JSON: ${error.message}` };
  }
}

// ---- servers ----

export const serverKey = (service, server) => `${service} ${server.path}`;
export const TRANSPORT_LABELS = { 'streamable-http': 'Streamable HTTP', sse: 'SSE', stdio: 'stdio', auto: 'Auto' };
export const SOURCE_LABELS = { 'service.json': 'declared', code: 'from code' };

// ---- the result ----

/** A content block for showing: `{ kind: 'text'|'image'|'audio'|'resource'|'link'|'other', ... }`. */
export function describeBlock(block) {
  if (!block || typeof block !== 'object') { return { kind: 'other', json: JSON.stringify(block) }; }
  if (block.type === 'text') { return { kind: 'text', text: String(block.text ?? '') }; }
  if (block.type === 'image' && block.data) { return { kind: 'image', src: `data:${block.mimeType || 'image/png'};base64,${block.data}`, mimeType: block.mimeType || 'image' }; }
  if (block.type === 'audio' && block.data) { return { kind: 'audio', src: `data:${block.mimeType || 'audio/wav'};base64,${block.data}`, mimeType: block.mimeType || 'audio' }; }
  if (block.type === 'resource' && block.resource) {
    const { resource } = block;
    return { kind: 'resource', uri: resource.uri, mimeType: resource.mimeType || null, text: resource.text ?? null, blobSize: resource.blob ? Math.floor((resource.blob.length * 3) / 4) : null };
  }
  if (block.type === 'resource_link') { return { kind: 'link', uri: block.uri, name: block.name || block.uri, description: block.description || null, mimeType: block.mimeType || null }; }
  return { kind: 'other', json: JSON.stringify(block, null, 2) };
}

/** What a call answered, in a few words (History). */
export function resultSummary(answer) {
  if (!answer) { return ''; }
  if (answer.error) { return `Error ${answer.error.code}: ${answer.error.message}`; }
  const result = answer.result || {};
  if (result.isError) { return 'isError'; }
  if (Array.isArray(result.content)) { return `${result.content.length} block${result.content.length === 1 ? '' : 's'}`; }
  if (Array.isArray(result.contents)) { return `${result.contents.length} content${result.contents.length === 1 ? '' : 's'}`; }
  if (Array.isArray(result.messages)) { return `${result.messages.length} message${result.messages.length === 1 ? '' : 's'}`; }
  return 'OK';
}

export const pretty = (value) => {
  if (typeof value === 'string') {
    try { return JSON.stringify(JSON.parse(value), null, 2); } catch { return value; }
  }
  return JSON.stringify(value, null, 2);
};

// ---- History: the calls of this session, in this browser tab ----

const historyKey = (projectId) => `flow.mcp.history.${projectId}`;

export function readHistory(projectId) {
  try {
    const list = JSON.parse(sessionStorage.getItem(historyKey(projectId)) || '[]');
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
}

export function addToHistory(projectId, entry) {
  const next = [{ id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, at: new Date().toISOString(), ...entry }, ...readHistory(projectId)].slice(0, HISTORY_MAX);
  try { sessionStorage.setItem(historyKey(projectId), JSON.stringify(next)); } catch { /* kept for this page only */ }
  return next;
}

export function clearHistory(projectId) {
  try { sessionStorage.removeItem(historyKey(projectId)); } catch { /* nothing kept */ }
  return [];
}

// ---- The token the person gives (this browser session only, never on the server, never in the project) ----

const tokenKey = (projectId) => `flow.mcp.token.${projectId}`;
const EMPTY_TOKEN = { name: 'Authorization', value: '', source: 'manual' };

/** `{ name, value, source: 'manual'|'oauth'|'secret' }`: kept in sessionStorage (a token kept by an older Flow in localStorage is dropped). */
export function readToken(projectId) {
  try { localStorage.removeItem(tokenKey(projectId)); } catch { /* nothing kept there */ }
  try {
    const value = JSON.parse(sessionStorage.getItem(tokenKey(projectId)) || 'null');
    return value && typeof value.name === 'string' ? { name: value.name, value: String(value.value || ''), source: value.source || 'manual' } : { ...EMPTY_TOKEN };
  } catch {
    return { ...EMPTY_TOKEN };
  }
}

export function saveToken(projectId, token) {
  try {
    if (!token.value) { sessionStorage.removeItem(tokenKey(projectId)); } else { sessionStorage.setItem(tokenKey(projectId), JSON.stringify({ name: token.name, value: token.value, source: token.source || 'manual' })); }
  } catch { /* not kept */ }
}

// ---- "This server needs a sign-in" ----

/** A token's header value: `scheme` (`Bearer ` or nothing) before the value, unless the value already has a scheme. */
export const headerValue = (scheme, value) => {
  const given = String(value || '').trim();
  if (!scheme || /^[A-Za-z][\w-]*\s+\S/.test(given)) { return given; }
  return `${scheme}${given}`;
};

/** A random token to save as a secret: 32 bytes, hex. */
export function generateToken() {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * The guide of a sign-in error (vfs-service `signIn`): `{ status, options: [{ kind, title, line, … }] }`. An older
 * vfs-service sends none: then a token (and an app user when the app has accounts).
 */
export function signInGuideOf(error, { hasUsers = false } = {}) {
  if (!error || error.code !== 'mcp_auth') { return null; }
  const given = error.body?.signIn;
  if (given && Array.isArray(given.options) && given.options.length) { return given; }
  const options = [];
  if (hasUsers) { options.push({ kind: 'user', title: 'An app user', line: 'Sign in as one of the app\'s own accounts: Flow signs in for you and sends its session.' }); }
  options.push({ kind: 'token', title: 'Use a token', line: 'Paste a token (sent as Authorization: Bearer …): kept for this browser session only, never stored in the project.', header: 'Authorization', scheme: 'Bearer ' });
  return { status: error.body?.httpStatus || 401, options };
}

/** What the transport choice says: Auto's rule before connecting, what it picked after. */
export function transportNote(transport, connection) {
  if (transport !== 'auto') { return null; }
  if (connection?.transport) { return `Auto picked ${TRANSPORT_LABELS[connection.transport] || connection.transport}`; }
  return 'Auto tries Streamable HTTP, then SSE';
}

/** Where the OAuth sign-in comes back: Flow's own page next to the app (public/mcp-oauth-callback.html). */
export const oauthRedirectUri = (origin = window.location.origin, base = import.meta.env.BASE_URL || '/') => `${origin}${base.endsWith('/') ? base : `${base}/`}mcp-oauth-callback.html`;

export const OAUTH_MESSAGE = 'flow-mcp-oauth';
